import * as Accordion from '@radix-ui/react-accordion';
import { take } from 'lodash';
import { CalendarRange, GitCompare, PackageSearch } from 'lucide-react';

import { Link } from '@/components/link';
import { OpenItemContent } from '@/components/modules/app-navigation/open/item/content';
import { OpenSavedItemsEmpty } from '@/components/modules/app-navigation/open/saved-items/empty-state';
import { SavedItem } from '@/components/modules/app-navigation/open/saved-items/item';
import { OpenSectionHeader } from '@/components/modules/app-navigation/open/section/header';
import { SAVED_ITEM_TYPE } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { getSavedItemLink } from '@/lib/models/saved-item/utils';
import { tv } from '@/utils/styles';

export interface OpenSavedItemsProps {
  savedItems: SavedItemModel[];
  onDelete: (savedItem: SavedItemModel) => void;
  visibility: 'visible' | 'hidden';
  isOpen?: boolean;
}

export const getSavedItemIcon = (savedItem: SavedItemModel) => {
  const iconSize = 12;

  if (savedItem.type === SAVED_ITEM_TYPE.ROUTE) return <GitCompare size={iconSize} />;

  if (savedItem.type === SAVED_ITEM_TYPE.SHIPMENT) return <PackageSearch size={iconSize} />;

  return <CalendarRange size={iconSize} />;
};

const OpenSavedItems = ({ savedItems, onDelete, visibility, isOpen }: OpenSavedItemsProps) => {
  const handleDelete = (savedItem: SavedItemModel) => {
    onDelete(savedItem);
  };

  const limitedSavedItems = take(savedItems, 6);
  const hasMoreItems = limitedSavedItems.length < savedItems.length;

  const { base, list, seeMore } = styles();

  return (
    <Accordion.Item value="saved-items" className={base()}>
      <OpenSectionHeader label="Favourites" isOpen={isOpen} visibility={visibility} />
      <OpenItemContent isOpen={isOpen} visibility={visibility}>
        {limitedSavedItems.length ? (
          <ul className={list()}>
            {limitedSavedItems.map((savedItem, i) => {
              return (
                <SavedItem
                  key={i}
                  label={savedItem.label}
                  type={savedItem.type}
                  icon={getSavedItemIcon(savedItem)}
                  href={getSavedItemLink(savedItem)}
                  onDelete={() => handleDelete(savedItem)}
                />
              );
            })}
            {hasMoreItems && (
              <li>
                <Link className={seeMore()} href="/dashboard">
                  See all favourites
                </Link>
              </li>
            )}
          </ul>
        ) : (
          <OpenSavedItemsEmpty />
        )}
      </OpenItemContent>
    </Accordion.Item>
  );
};

const styles = tv({
  slots: {
    base: 'mt-4 flex flex-col justify-center [&_.trigger-icon]:data-[state=open]:rotate-180',
    list: 'flex flex-col pb-4',
    header: 'flex items-center gap-px text-sm opacity-70',
    icon: 'trigger-icon leading-none transition-all duration-200',
    seeMore: 'mt-0.5 block px-2 text-sm italic opacity-50 hover:underline hover:opacity-100',
  },
});

export { OpenSavedItems };
